/* eslint-disable */

import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { openAuthenticatedPdf } from "../lib/pdfDownloader";
import { Button } from "../components/ui/button";
import { ArrowLeft, Briefcase, Calendar, Download, Eye, FileText, Loader2, Mail, MapPin, Phone, TrendingUp, AlertCircle, UserCircle2, CheckCircle2, XCircle, Percent, Send, Link2 } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { formatApiErrorDetail } from "../lib/auth";
import CorrespondenceSection from "../components/resellers/CorrespondenceSection";
import ResellerBalanceWidget from "../components/resellers/ResellerBalanceWidget";
import { ResellerAccountPanel } from "../components/resellers/ResellerAccountPanel";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-CH", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function daysAgo(iso) {
  if (!iso) return null;
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 86400000;
    return Math.floor(diff);
  } catch { return null; }
}

export default function ResellerDetail() {
  const { id } = useParams();
  const [reseller, setReseller] = useState(null);
  const [activity, setActivity] = useState(null);
  const [linkedCustomer, setLinkedCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  // Phase 32-bis : envoi du PDF par email
  const [emailDialog, setEmailDialog] = useState({ open: false, message: "", sending: false, customEmail: "" });
  // Phase 66 — liaison vers un distributeur ERP (pour auto-fill reseller_id sur cmd ERP)
  const [distributors, setDistributors] = useState([]);
  const [linkSaving, setLinkSaving] = useState(false);
  // Phase 66d — divergence Reseller ↔ Distributor lié
  const [diff, setDiff] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const loadDiff = async (reseller) => {
    if (!reseller?.linked_distributor_id) { setDiff(null); return; }
    try {
      const { data } = await api.get(`/resellers/${reseller.id}/distributor-diff`);
      setDiff(data);
    } catch {
      setDiff(null);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [r, a, lc, d] = await Promise.all([
        api.get(`/resellers?include_archived=true`),
        api.get(`/resellers/${id}/activity`),
        api.get(`/resellers/${id}/linked-customer`).catch(() => ({ data: { customer: null } })),
        api.get(`/distributors`).catch(() => ({ data: [] })),
      ]);
      const rs = (r.data || []).find((x) => x.id === id) || null;
      setReseller(rs);
      setActivity(a.data);
      setLinkedCustomer(lc.data?.customer || null);
      setDistributors(d.data || []);
      // Charge le diff après que le reseller soit chargé
      await loadDiff(rs);
    } finally {
      setLoading(false);
    }
  };

  const saveLinkedDistributor = async (distributorId) => {
    setLinkSaving(true);
    try {
      await api.put(`/resellers/${id}`, { linked_distributor_id: distributorId || null });
      toast.success(distributorId ? "Distributeur ERP lié" : "Liaison supprimée");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setLinkSaving(false);
    }
  };

  const syncFromDistributor = async () => {
    if (!window.confirm(
      "Copier les coordonnées (nom, adresse, contact) depuis le distributeur lié ?\n\n"
      + "Les champs spécifiques shop (remise, catalogue, prix override) sont conservés."
    )) return;
    setSyncing(true);
    try {
      const { data } = await api.post(`/resellers/${id}/sync-from-distributor`);
      toast.success(data.message || "Synchronisation effectuée");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setSyncing(false);
    }
  };

  // Phase 101ck (18/02/2026) — Crée le compte distributeur CLIENT lié pour ce
  // revendeur (backfill des revendeurs créés avant l'auto-création).
  const [creatingDist, setCreatingDist] = useState(false);
  const createLinkedDistributor = async () => {
    setCreatingDist(true);
    try {
      const { data } = await api.post(`/resellers/${id}/create-linked-distributor`);
      const msg = data.created
        ? `Compte client « ${data.distributor_name} » créé et lié · BL débloqué`
        : data.reused_existing
          ? `Compte client existant « ${data.distributor_name} » réutilisé et lié`
          : "Déjà lié";
      toast.success(msg);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setCreatingDist(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

  const onPdf = async (action) => {
    setGenerating(true);
    try {
      const slug = (reseller?.name || "revendeur").replace(/[^a-z0-9]/gi, "_");
      const filename = `tarifs-revendeurs-${slug}-${new Date().toISOString().slice(0, 10)}.pdf`;
      await openAuthenticatedPdf({
        url: "/api/price-lists/reseller.pdf",
        action,
        filename,
        fetchOptions: {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ reseller_id: id, action }),
        },
      });
      await load(); // refresh activity log
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert("Erreur : " + e.message);
    } finally {
      setGenerating(false);
    }
  };

  const stats = useMemo(() => {
    if (!activity) return [];
    return [
      { label: "Catalogue", value: activity.catalog_count, sub: activity.catalog_count > 0 ? "plats proposés" : "tous (défaut)", color: "#1F4E3D" },
      { label: "Prix overrides", value: activity.overrides_count, sub: activity.overrides_count > 0 ? "personnalisés" : "prix standards", color: "#8C6B2F" },
      { label: "Marge moyenne", value: activity.margin_avg_percent != null ? `${activity.margin_avg_percent}%` : "—", sub: "sur le catalogue", color: "#A04E2C" },
      { label: "PDF générés (30 j)", value: activity.pdf_30d, sub: `${activity.pdf_total} total`, color: "#3D2914" },
    ];
  }, [activity]);

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#8C6B2F]" /></div>;
  }

  if (!reseller) {
    return (
      <div className="space-y-4" data-testid="reseller-detail-notfound">
        <Link to="/revendeurs" className="text-sm text-[#1F4E3D] hover:underline flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Retour</Link>
        <div className="bg-white border border-[#E5E5E0] rounded-lg p-8 text-center text-[#A3A6A3] italic">Revendeur introuvable.</div>
      </div>
    );
  }

  const lastUpdateDays = daysAgo(reseller.updated_at);
  const lastPdfDays = daysAgo(activity?.last_pdf_at);

  return (
    <div className="space-y-6" data-testid="reseller-detail-page">
      {/* Header */}
      <div>
        <Link to="/revendeurs" className="text-sm text-[#1F4E3D] hover:underline flex items-center gap-1 mb-3"><ArrowLeft className="w-4 h-4" /> Tous les revendeurs</Link>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <Briefcase className="w-7 h-7 text-[#8C6B2F] flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-2xl font-heading">{reseller.name}</h2>
              {reseller.legal_name && <p className="text-sm text-[#A3A6A3]">{reseller.legal_name}</p>}
              {reseller.archived && <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full bg-[#FBE5E0] text-[#A04E2C] border border-[#E8C7BD]">Archivé</span>}
              {activity?.is_dormant && !reseller.archived && (
                <span className="inline-flex items-center gap-1 mt-1 text-xs px-2 py-0.5 rounded-full bg-[#FBEEDB] text-[#8C6B2F] border border-[#E8D3A8]">
                  <AlertCircle className="w-3 h-3" /> Dormant — aucune activité depuis 90 jours
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button variant="outline" onClick={() => onPdf("preview")} disabled={generating} data-testid="detail-preview-pdf">
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />} Aperçu tarif
            </Button>
            <Button onClick={() => onPdf("download")} disabled={generating} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="detail-download-pdf">
              <Download className="w-4 h-4 mr-2" /> Télécharger PDF
            </Button>
            <Button
              variant="outline"
              onClick={() => setEmailDialog({ open: true, message: "", sending: false, customEmail: reseller.email || "" })}
              disabled={generating}
              className="border-[#8C6B2F] text-[#8C6B2F] hover:bg-[#FBF4E9]"
              data-testid="detail-send-pricelist"
              title="Envoyer le PDF par email avec un mot personnel"
            >
              <Send className="w-4 h-4 mr-2" /> Envoyer au revendeur
            </Button>
          </div>
        </div>
      </div>

      {/* Phase 32-bis : Dialog d'envoi des tarifs */}
      <Dialog open={emailDialog.open} onOpenChange={(o) => !o && !emailDialog.sending && setEmailDialog((d) => ({ ...d, open: false }))}>
        <DialogContent className="sm:max-w-lg" data-testid="send-pricelist-dialog">
          <DialogHeader>
            <DialogTitle>Envoyer les tarifs à {reseller.name}</DialogTitle>
            <DialogDescription>
              Le PDF des tarifs négociés sera envoyé en pièce jointe. Vous pouvez ajouter un mot personnel.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#575A57] font-semibold block mb-1">
                Destinataire
              </label>
              <input
                type="email"
                value={emailDialog.customEmail}
                onChange={(e) => setEmailDialog((d) => ({ ...d, customEmail: e.target.value }))}
                disabled={emailDialog.sending}
                className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm font-mono"
                placeholder="contact@exemple.ch"
                data-testid="send-pricelist-email-input"
              />
              {!reseller.email && (
                <p className="text-[11px] text-[#A04E2C] mt-1">⚠ Aucun email n'est encore enregistré sur la fiche revendeur</p>
              )}
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#575A57] font-semibold block mb-1">
                Message personnel (optionnel)
              </label>
              <Textarea
                value={emailDialog.message}
                onChange={(e) => setEmailDialog((d) => ({ ...d, message: e.target.value }))}
                disabled={emailDialog.sending}
                placeholder="Cher Marc,&#10;&#10;Voici vos nouveaux tarifs pour la saison 2026. Vous remarquerez deux nouveaux plats au menu — n'hésitez pas à me dire ce que vous en pensez !&#10;&#10;À bientôt,"
                rows={6}
                className="text-sm"
                data-testid="send-pricelist-message"
              />
              <p className="text-[11px] text-[#A3A6A3] mt-1">
                Ce texte apparaîtra dans le corps de l'email, au-dessus du lien vers le PDF. Sauts de ligne respectés.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEmailDialog((d) => ({ ...d, open: false }))}
              disabled={emailDialog.sending}
            >
              Annuler
            </Button>
            <Button
              onClick={async () => {
                if (!emailDialog.customEmail.trim() || !emailDialog.customEmail.includes("@")) {
                  toast.error("Email invalide");
                  return;
                }
                setEmailDialog((d) => ({ ...d, sending: true }));
                try {
                  const { data } = await api.post(`/resellers/${reseller.id}/send-pricelist`, {
                    reseller_id: reseller.id,
                    personal_message: emailDialog.message,
                    to_email: emailDialog.customEmail.trim(),
                  });
                  toast.success(`Tarifs envoyés à ${data.recipient} · ${data.product_count} produits`);
                  setEmailDialog({ open: false, message: "", sending: false, customEmail: "" });
                  load(); // refresh activity (PDF count)
                } catch (e) {
                  toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur d'envoi");
                  setEmailDialog((d) => ({ ...d, sending: false }));
                }
              }}
              disabled={emailDialog.sending || !emailDialog.customEmail.trim()}
              className="bg-[#1F4E3D] hover:bg-[#173B2E]"
              data-testid="send-pricelist-confirm"
            >
              {emailDialog.sending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi en cours…</>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Envoyer</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.map((s, i) => (
          <div key={i} className="bg-white border border-[#E5E5E0] rounded-lg p-4" data-testid={`stat-card-${i}`}>
            <div className="text-xs uppercase tracking-wider text-[#575A57] font-semibold">{s.label}</div>
            <div className="text-2xl font-heading mt-1" style={{ color: s.color }}>{s.value}</div>
            <div className="text-xs text-[#A3A6A3] mt-0.5">{s.sub}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Coordonnées */}
        <section className="bg-white border border-[#E5E5E0] rounded-lg p-5 lg:col-span-1">
          <h3 className="font-heading text-base mb-3">Coordonnées</h3>
          <div className="space-y-2 text-sm">
            {reseller.contact_name && <div><span className="text-xs text-[#A3A6A3] uppercase tracking-wider">Contact</span><div>{reseller.contact_name}</div></div>}
            {reseller.email && <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-[#8C6B2F]" /><a href={`mailto:${reseller.email}`} className="text-[#1F4E3D] hover:underline">{reseller.email}</a></div>}
            {reseller.billing_email && reseller.billing_email !== reseller.email && (
              <div className="flex items-center gap-2" data-testid="reseller-detail-billing-email">
                <Mail className="w-4 h-4 text-[#A04E2C]" />
                <a href={`mailto:${reseller.billing_email}`} className="text-[#A04E2C] hover:underline">
                  {reseller.billing_email}
                </a>
                <span className="text-[10px] uppercase tracking-wider text-[#A04E2C] font-semibold bg-[#FBEEEA] px-1.5 py-0.5 rounded border border-[#E8B0A0]">
                  Facturation
                </span>
              </div>
            )}
            {reseller.phone && <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-[#8C6B2F]" /><a href={`tel:${reseller.phone}`} className="text-[#1F4E3D] hover:underline">{reseller.phone}</a></div>}
            {(reseller.address || reseller.city) && (
              <div className="flex items-start gap-2"><MapPin className="w-4 h-4 text-[#8C6B2F] mt-0.5 flex-shrink-0" />
                <div>
                  {reseller.address && <div>{reseller.address}</div>}
                  {(reseller.postal_code || reseller.city) && <div>{reseller.postal_code} {reseller.city}</div>}
                </div>
              </div>
            )}
            <div className="pt-3 border-t border-[#F0F0EB] text-xs text-[#575A57]">
              <div className="flex items-center gap-2"><Calendar className="w-3 h-3 text-[#A3A6A3]" /> Créé le {fmtDate(reseller.created_at)}</div>
              <div className="flex items-center gap-2 mt-1"><TrendingUp className="w-3 h-3 text-[#A3A6A3]" /> Modifié il y a {lastUpdateDays != null ? `${lastUpdateDays} j` : "—"}</div>
            </div>
            {reseller.notes && (
              <div className="pt-3 border-t border-[#F0F0EB]">
                <div className="text-xs text-[#A3A6A3] uppercase tracking-wider mb-1">Notes commerciales</div>
                <p className="text-xs whitespace-pre-line text-[#3D2914]">{reseller.notes}</p>
              </div>
            )}
            <div className="pt-3">
              <Link to="/revendeurs" className="text-xs text-[#1F4E3D] hover:underline">→ Modifier dans la liste</Link>
            </div>
          </div>
        </section>

        {/* Phase 31 : Tarification B2B + Compte boutique lié */}
        <section className="bg-white border border-[#E5E5E0] rounded-lg p-5 lg:col-span-2 space-y-4">
          <h3 className="font-heading text-base flex items-center gap-2">
            <Percent className="w-4 h-4 text-[#1F4E3D]" /> Tarification & accès boutique
          </h3>

          {/* Rabais */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-[#EAF3EE] border border-[#C6DFCC] rounded p-3">
              <div className="text-[10px] uppercase tracking-wider text-[#1F4E3D] font-semibold mb-1">Rabais global</div>
              {reseller.discount_percent != null ? (
                <div className="text-2xl font-bold text-[#1F4E3D]">−{reseller.discount_percent}%</div>
              ) : (
                <div className="text-sm text-[#575A57] italic">Aucun</div>
              )}
              <div className="text-[10px] text-[#575A57] mt-1">sur tous les produits</div>
            </div>
            <div className="bg-[#FBF4E9] border border-[#E8D5A9] rounded p-3">
              <div className="text-[10px] uppercase tracking-wider text-[#8C6B2F] font-semibold mb-1">Prix négociés individuels</div>
              <div className="text-2xl font-bold text-[#8C6B2F]">{Object.keys(reseller.price_overrides || {}).length}</div>
              <div className="text-[10px] text-[#575A57] mt-1">produit(s) avec prix override</div>
            </div>
            <div className="bg-white border border-[#E5E5E0] rounded p-3">
              <div className="text-[10px] uppercase tracking-wider text-[#575A57] font-semibold mb-1">Catalogue proposé</div>
              <div className="text-2xl font-bold text-[#3D2914]">
                {(reseller.displayed_product_ids || []).length || "Tous"}
              </div>
              <div className="text-[10px] text-[#575A57] mt-1">produit(s) affichés</div>
            </div>
          </div>

          {/* Compte boutique lié */}
          <div className="border-t border-[#F0F0EB] pt-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs uppercase tracking-wider text-[#575A57] font-semibold flex items-center gap-1.5">
                <UserCircle2 className="w-3.5 h-3.5 text-[#1F4E3D]" /> Compte boutique lié
              </h4>
            </div>
            {linkedCustomer ? (
              <div className="bg-[#FAFAF8] rounded p-3 flex items-center justify-between gap-3" data-testid="linked-customer-card">
                <div className="min-w-0">
                  <div className="font-medium text-[#3D2914]">{linkedCustomer.name}</div>
                  <div className="text-xs text-[#575A57]">{linkedCustomer.email}</div>
                  <div className="text-[11px] text-[#A3A6A3] mt-0.5">
                    Inscrit le {fmtDate(linkedCustomer.created_at)}
                    {linkedCustomer.last_login_at && ` · Dernière connexion ${fmtDate(linkedCustomer.last_login_at)}`}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  {linkedCustomer.email_verified ? (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#EAF3EE] text-[#1F4E3D] border border-[#C6DFCC]">
                      <CheckCircle2 className="w-3 h-3" /> Vérifié
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#FBE5E0] text-[#A04E2C] border border-[#E8C7BD]">
                      <XCircle className="w-3 h-3" /> Non vérifié
                    </span>
                  )}
                  {linkedCustomer.disabled && (
                    <span className="text-[10px] text-[#A3A6A3]">Désactivé</span>
                  )}
                  <Link to="/parametres/clients-boutique" className="text-[11px] text-[#1F4E3D] hover:underline">→ Gérer</Link>
                </div>
              </div>
            ) : (
              <div className="bg-[#FAFAF8] border border-dashed border-[#E5E5E0] rounded p-3 text-sm text-[#575A57]" data-testid="no-linked-customer">
                <p className="leading-snug">
                  Aucun compte boutique lié pour le moment.
                </p>
                {reseller.email ? (
                  <p className="text-xs mt-1.5 text-[#1F4E3D]">
                    💡 Le revendeur peut s'inscrire sur <code className="bg-white border border-[#E5E5E0] px-1.5 py-0.5 rounded">/boutique/inscription</code> avec l'email <strong>{reseller.email}</strong> — le lien sera fait automatiquement.
                  </p>
                ) : (
                  <p className="text-xs mt-1.5 text-[#A04E2C]">
                    ⚠ Ajoutez un email à ce revendeur pour permettre l'auto-lien à l'inscription boutique.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Phase 66 — Liaison vers un distributeur ERP (pour auto-fill reseller_id sur cmd manuelle) */}
          <div className="border-t border-[#F0F0EB] pt-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs uppercase tracking-wider text-[#575A57] font-semibold flex items-center gap-1.5">
                <Link2 className="w-3.5 h-3.5 text-[#1F4E3D]" /> Distributeur ERP lié
              </h4>
            </div>
            <div className="bg-[#FAFAF8] rounded p-3" data-testid="linked-distributor-block">
              <p className="text-[11px] text-[#575A57] mb-2 leading-snug">
                Liez ce revendeur à son distributeur (client ERP) pour que les commandes créées manuellement via <code className="bg-white border border-[#E5E5E0] px-1 rounded text-[10px]">/commandes-clients</code> soient automatiquement marquées comme commande revendeur (mode <strong>fabrication dédiée</strong>).
              </p>
              <select
                value={reseller.linked_distributor_id || ""}
                onChange={(e) => saveLinkedDistributor(e.target.value)}
                disabled={linkSaving}
                className="w-full bg-white border border-[#E5E5E0] rounded px-3 py-2 text-sm focus:outline-none focus:border-[#1F4E3D] disabled:opacity-50"
                data-testid="linked-distributor-select"
              >
                <option value="">— Aucun distributeur lié —</option>
                {distributors.filter((d) => !d.is_archived).map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name} {d.code ? `(${d.code})` : ""} {d.type ? `· ${d.type}` : ""}
                  </option>
                ))}
              </select>
              {linkSaving && (
                <div className="flex items-center gap-1.5 text-[11px] text-[#1F4E3D] mt-1.5">
                  <Loader2 className="w-3 h-3 animate-spin" /> Enregistrement…
                </div>
              )}
              {/* Phase 101ck (18/02/2026) — Bouton « Créer et lier » quand aucun distributeur
                  n'est encore lié (débloque la création de BL pour ce revendeur). */}
              {!reseller.linked_distributor_id && (
                <Button
                  size="sm"
                  onClick={createLinkedDistributor}
                  disabled={creatingDist}
                  className="mt-2 w-full bg-[#1F4E3D] hover:bg-[#173B2E] text-white"
                  data-testid="create-linked-distributor-btn"
                >
                  {creatingDist ? (
                    <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> Création…</>
                  ) : (
                    <>+ Créer le compte client lié (débloque BL & facture)</>
                  )}
                </Button>
              )}
              {reseller.linked_distributor_id && (
                <p className="text-[10px] text-[#1F4E3D] mt-2" data-testid="linked-distributor-active">
                  ✓ Mode fabrication dédiée actif pour les futures commandes ERP de ce distributeur.
                </p>
              )}

              {/* Phase 66d — Détection divergence et bouton de synchronisation */}
              {diff?.linked && diff.has_diff && (
                <div className="mt-3 p-3 bg-[#FCEEEA] border border-[#E8C1B4] rounded" data-testid="distributor-diff-block">
                  <div className="flex items-start gap-2 mb-2">
                    <AlertCircle className="w-4 h-4 text-[#C05C47] mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] text-[#C05C47] font-semibold">
                        Divergence avec le distributeur lié
                      </p>
                      <p className="text-[10px] text-[#8C3D2F] mt-0.5">
                        {Object.keys(diff.fields).length} champ(s) diffèrent entre ce revendeur et "{diff.distributor_name}".
                      </p>
                    </div>
                  </div>
                  <table className="w-full text-[10px] border-collapse mb-2" data-testid="distributor-diff-table">
                    <thead>
                      <tr className="text-[#8C3D2F]">
                        <th className="text-left py-1 pr-2">Champ</th>
                        <th className="text-left py-1 pr-2">Revendeur</th>
                        <th className="text-left py-1">Distributeur</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(diff.fields).map(([field, vals]) => (
                        <tr key={field} className="border-t border-[#E8C1B4]">
                          <td className="py-1 pr-2 font-mono text-[#575A57] uppercase tracking-wider text-[9px]">{field}</td>
                          <td className="py-1 pr-2 text-[#1A1C1A]">{vals.reseller || <span className="text-[#A3A6A3] italic">vide</span>}</td>
                          <td className="py-1 text-[#1F4E3D] font-semibold">{vals.distributor || <span className="text-[#A3A6A3] italic">vide</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <Button
                    size="sm"
                    onClick={syncFromDistributor}
                    disabled={syncing}
                    className="w-full bg-[#C05C47] hover:bg-[#A04A38] text-white text-xs"
                    data-testid="sync-from-distributor-btn"
                  >
                    {syncing ? <><Loader2 className="w-3 h-3 animate-spin mr-1.5" /> Synchronisation…</> : <>Importer les valeurs du distributeur</>}
                  </Button>
                </div>
              )}
              {diff?.linked && !diff.has_diff && (
                <p className="text-[10px] text-[#1F4E3D] mt-2 flex items-center gap-1" data-testid="distributor-diff-clean">
                  <CheckCircle2 className="w-3 h-3" /> Données synchronisées avec le distributeur lié.
                </p>
              )}
            </div>
          </div>
        </section>

        {/* Historique PDF */}
        <section className="bg-white border border-[#E5E5E0] rounded-lg p-5 lg:col-span-3">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-base flex items-center gap-2"><FileText className="w-4 h-4 text-[#8C6B2F]" /> Historique des PDF tarifs</h3>
            <span className="text-xs text-[#A3A6A3]">{activity?.pdf_total || 0} génération{(activity?.pdf_total || 0) > 1 ? "s" : ""}</span>
          </div>
          {!activity?.recent_logs?.length ? (
            <p className="text-sm text-[#A3A6A3] italic text-center py-8">
              Aucun PDF généré pour ce revendeur. Cliquez sur « Télécharger PDF » ci-dessus pour démarrer le suivi.
            </p>
          ) : (
            <div className="space-y-1 max-h-96 overflow-y-auto">
              {activity.recent_logs.map((log) => (
                <div key={log.id} className="flex items-center justify-between gap-2 text-xs py-2 px-2 hover:bg-[#FAFAF8] rounded" data-testid={`pdf-log-${log.id}`}>
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <span className={`inline-block w-2 h-2 rounded-full ${log.action === "preview" ? "bg-[#8C6B2F]" : "bg-[#1F4E3D]"}`}></span>
                    <span className="font-medium text-[#3D2914] capitalize">{log.action === "preview" ? "Aperçu" : "Téléchargement"}</span>
                    <span className="text-[#A3A6A3]">par {log.user_email || "—"}</span>
                    <span className="text-[#A3A6A3]">· {log.product_count} plat{log.product_count > 1 ? "s" : ""}</span>
                  </div>
                  <span className="text-[#575A57] flex-shrink-0">{fmtDate(log.generated_at)}</span>
                </div>
              ))}
            </div>
          )}
          {activity?.last_pdf_at && (
            <p className="text-[11px] text-[#A3A6A3] mt-3 pt-3 border-t border-[#F0F0EB]">
              Dernier PDF : {lastPdfDays === 0 ? "aujourd'hui" : `il y a ${lastPdfDays} j`} · {fmtDate(activity.last_pdf_at)}
            </p>
          )}
        </section>

        {/* Phase 101fk (Feb 2026) — Widget Balance compte client */}
        <ResellerBalanceWidget resellerId={reseller.id} />

        <ResellerAccountPanel resellerId={reseller.id} />

        {/* Phase 78 — Correspondance officielle (lettres corporate Maison Thaï) */}
        <CorrespondenceSection resellerId={reseller.id} resellerName={reseller.name} />
      </div>
    </div>
  );
}
